import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { IFieldValueConfigurationModel } from '../../../../models/app-setting/app-setting.model';

export interface IChildNodeChip {
  name: string;
  displayName?: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  borderThickness?: number;
}
import { EditPanelUiHelperService } from '../../services/edit-panel-ui.helper';
import { Popover } from 'primeng/popover';
import { ExternalLinkStore } from '../../../../services/stores/external-link.store';
import { NextGenAppNavigationHelper } from '../../../../services/helpers/nextgen-app-navigation.helper';
import { WorkspaceStore } from '../../../../services/stores/workspace.store';
import { BACKLOG_ROLLUP_STATUS_READONLY_TOOLTIP, CATCH_INTERNAL_APP_WIKI_LOWER } from '../../../catch-core/data/constants/app.constants';
import { EDIT_PAGE_TOOLTIP, WIKI_APP_NOT_CONFIGURED, intelliCatchGenerateTestsTooltip } from '../../../catch-core/data/constants/ui-message.constants';
import { PAGE_NODE_LOWER } from '../../../catch-core/data/constants/blueprint.constants';
import { IntelliCatchItemContextService } from '../../../../services/intellicatch-item-context.service';
import { IntelliCatchAvailabilityState } from '../../../../models/copilot/intellicatch-availability.enum';
import { INodeInstanceModel } from '../../../../models/node/node-instance.model';

@Component({
  selector: 'app-edit-panel-header',
  templateUrl: './edit-panel-header.component.html',
  styleUrls: ['./edit-panel-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class EditPanelHeaderComponent {
  @Input() nodeKey?: string;
  @Input() isTestStepSelected = false;
  @Input() isDoView = false;
  @Input() childNodeNameList: IChildNodeChip[] = [];

  @Input() nodeStatusIcons: IFieldValueConfigurationModel[] = [];
  @Input() nodeStatusValue = '';
  @Input() nodeStatusLabel = 'Select';
  @Input() nodeStatusTooltip = '';
  @Input() nodeStatusChipStyle: Record<string, string> = {};

  @Input() stepStatusIcons: IFieldValueConfigurationModel[] = [];
  @Input() stepStatusValue = '';
  @Input() stepStatusLabel = 'Select status';
  @Input() stepStatusChipStyle: Record<string, string> = {};

  @Input() itemType = '';
  @Input() itemLabel = '';
  @Input() itemBackgroundColor = '';
  @Input() itemColor = '';
  @Input() itemBorderStyle: Record<string, string> = {};
  @Input() itemTooltip = '';
  @Input() summary = '';
  @Input() showNodeStatus = true;
  /** The status shown is another label's (Backlog roll-up), so it cannot be changed from here. */
  @Input() nodeStatusReadOnly = false;
  public readonly backlogRollupStatusTooltip = BACKLOG_ROLLUP_STATUS_READONLY_TOOLTIP;
  /** Same, for the selected test step's status. */
  @Input() stepStatusReadOnly = false;
  @Input() showStepStatus = false;
  @Input() hideNavigation = false;

  @Input() selectedNode: INodeInstanceModel | null = null;
  @Input() generateTestsState: IntelliCatchAvailabilityState = IntelliCatchAvailabilityState.NotApplicable;
  @Input() generateTestsBlockedReason = '';

  @ViewChild('summaryInput') summaryInputElement?: ElementRef<HTMLInputElement>;
  @ViewChild('createChildMenu') createChildMenuPopover?: Popover;

  public readonly isWikiAppConfigured$ = this.externalLinkStore.$isWikiAppConfigured;
  public readonly editPageTooltip = EDIT_PAGE_TOOLTIP;
  public readonly wikiAppNotConfigured = WIKI_APP_NOT_CONFIGURED;

  public get isPageItem(): boolean {
    return (this.itemType || '').toLowerCase() === PAGE_NODE_LOWER;
  }

  @Output() summaryChange = new EventEmitter<string>();
  @Output() summaryBlur = new EventEmitter<string>();
  @Output() navigatePrevious = new EventEmitter<void>();
  @Output() navigateNext = new EventEmitter<void>();
  @Output() openChildMenu = new EventEmitter<void>();
  @Output() addTestStep = new EventEmitter<void>();
  @Output() addChildNode = new EventEmitter<string>();
  @Output() closePanel = new EventEmitter<void>();
  @Output() changeNodeStatus = new EventEmitter<string>();
  @Output() changeStepStatus = new EventEmitter<string>();

  constructor(
    private uiHelper: EditPanelUiHelperService,
    private host: ElementRef<HTMLElement>,
    private externalLinkStore: ExternalLinkStore,
    private nextGenAppNavigationHelper: NextGenAppNavigationHelper,
    private workspaceStore: WorkspaceStore,
    private intelliCatchItemContextService: IntelliCatchItemContextService,
  ) {}

  public get canShowGenerateTests(): boolean {
    return this.generateTestsState !== IntelliCatchAvailabilityState.NotApplicable && !(this.isTestStepSelected && this.isDoView);
  }

  public get isGenerateTestsBlocked(): boolean {
    return this.generateTestsState === IntelliCatchAvailabilityState.Blocked;
  }

  public get generateTestsTooltip(): string {
    return this.isGenerateTestsBlocked ? this.generateTestsBlockedReason : intelliCatchGenerateTestsTooltip(this.selectedNode?.key);
  }

  public onGenerateTestsClicked(): void {
    if (!this.selectedNode || this.isGenerateTestsBlocked) {
      return;
    }
    this.intelliCatchItemContextService.startSession(this.selectedNode);
  }

  public onSummaryInput(value: string): void {
    this.summaryChange.emit(value);
  }

  public onNodeStatusSelect(status: string, popover: any): void {
    this.changeNodeStatus.emit(status);
    popover?.hide?.();
  }

  public onStepStatusSelect(status: string, popover: any): void {
    this.changeStepStatus.emit(status);
    popover?.hide?.();
  }

  public onAddChildNode(childNodeChip: IChildNodeChip, popover: any): void {
    this.addChildNode.emit(childNodeChip.name);
    popover?.hide?.();
  }

  public onTriggerChildMenu(popover: any, event: Event): void {
    this.openChildMenu.emit();
    popover?.toggle?.(event);
  }

  public toggleChildMenu(event?: Event): void {
    this.openChildMenu.emit();
    this.createChildMenuPopover?.toggle(event);
  }

  public onChildMenuShow(): void {
    const container = this.createChildMenuPopover?.container as HTMLElement | undefined;
    if (!container) {
      return;
    }

    const panelRect = this.getParentPanelRect();
    if (!panelRect) {
      return;
    }

    const overlayRect = container.getBoundingClientRect();
    const padding = 8;
    let nextLeft = overlayRect.left;
    let nextTop = overlayRect.top;

    const overflowRight = overlayRect.right - panelRect.right;
    if (overflowRight > 0) {
      nextLeft = overlayRect.left - overflowRight - padding;
    }

    const overflowLeft = panelRect.left - nextLeft;
    if (overflowLeft > 0) {
      nextLeft = panelRect.left + padding;
    }

    const overflowBottom = overlayRect.bottom - panelRect.bottom;
    if (overflowBottom > 0) {
      nextTop = overlayRect.top - overflowBottom - padding;
    }

    const overflowTop = panelRect.top - nextTop;
    if (overflowTop > 0) {
      nextTop = panelRect.top + padding;
    }

    const hasShifted = Math.abs(nextLeft - overlayRect.left) > 0.5 || Math.abs(nextTop - overlayRect.top) > 0.5;
    if (hasShifted) {
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;
      container.style.left = `${nextLeft + scrollX}px`;
      container.style.top = `${nextTop + scrollY}px`;
      this.realignPopoverArrow(container);
    }
  }

  public getChildChipBorderStyle(chip: IChildNodeChip): Record<string, string> {
    const color = chip.borderColor;
    const thickness = chip.borderThickness;
    return {
      'border-color': color || 'transparent',
      'border-width': (color ? (thickness && thickness > 0 ? thickness : 1) : 0) + 'px',
      'border-style': color ? 'solid' : 'none',
    };
  }

  public isNodeStatusSelected(status: string): boolean {
    return this.uiHelper.isStatusSelected(status, this.nodeStatusValue);
  }

  public isStepStatusSelected(status: string): boolean {
    return this.uiHelper.isStatusSelected(status, this.stepStatusValue);
  }

  public focusSummary(selectAll = false): void {
    this.uiHelper.focusAndSelect(this.summaryInputElement?.nativeElement, selectAll);
  }

  public onCreateChildNodeClicked(event?: Event): void {
    this.toggleChildMenu(event);
  }

  public onEditPageClicked(event?: Event): void {
    const app = this.externalLinkStore.getAppLink(CATCH_INTERNAL_APP_WIKI_LOWER);
    if (app) {
      this.nextGenAppNavigationHelper.loadNextgenApplication(app);
      this.workspaceStore.isShowWikiEditor = true;
    }
  }

  private getParentPanelRect(): DOMRect | null {
    const hostElement = this.host?.nativeElement;
    if (!hostElement) {
      return null;
    }
    const panel = hostElement.closest('.parent-panel') as HTMLElement | null;
    return panel?.getBoundingClientRect() ?? null;
  }

  private realignPopoverArrow(container: HTMLElement): void {
    const targetEl = (this.createChildMenuPopover as any)?.target as HTMLElement | null;
    if (!targetEl) {
      return;
    }
    const overlayRect = container.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const radius = parseFloat(getComputedStyle(container).borderRadius || '0') || 0;
    const arrowLeft = Math.max(0, targetRect.left - overlayRect.left - radius * 2 + targetRect.width / 2);
    container.style.setProperty('--p-popover-arrow-left', `${arrowLeft}px`);
  }
}
